/**
 * A session timer's check-ins (timer spec, "Runaway timers" and "Past the plan"), kept in this browser per session so a
 * tab back from a sleeping laptop still knows them:
 * - the last check-in answered (0 = the plan's end), by "Keep going" or by resuming, which sets when it pauses itself;
 * - the laps the student said they kept going through ("Keep going" at a lap's end), ticked when it paused itself;
 * - when it paused itself, for "Paused itself" and the "Since it paused" lap.
 * The top bar's pill and the session popup both answer, so each hears when the other did.
 */

export type PausedItself = {
  /** When it paused, as the clock on the wall: a tab back from a sleeping laptop pauses it back then. */
  at: number;
  /** The time it had counted then. */
  seconds: number;
};

export type TimerCheckIns = {
  answered: number;
  kept: number[];
  pausedItself?: PausedItself;
};

// The last answered stays a plain number under its old key, as it has been kept since 2026-10-16.
const answeredKey = (plannedTaskId: number) => `shovel.timerCheckIn.${plannedTaskId}`;
const lapsKey = (plannedTaskId: number) => `shovel.timerLaps.${plannedTaskId}`;

const listeners = new Set<(plannedTaskId: number) => void>();
const tell = (plannedTaskId: number) => listeners.forEach(listener => listener(plannedTaskId));

/** Called whenever a session's check-ins change, here or in another part of the page. */
export const onCheckIns = (listener: (plannedTaskId: number) => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

type Laps = { kept: number[]; pausedItself?: PausedItself };

const readLaps = (plannedTaskId: number): Laps => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(lapsKey(plannedTaskId)) || 'null');
    if (!parsed || typeof parsed !== 'object') return { kept: [] };
    const kept = Array.isArray(parsed.kept) ? parsed.kept.filter((lap: unknown) => Number.isInteger(lap)) : [];
    const paused = parsed.pausedItself;
    const pausedItself =
      paused && Number.isFinite(paused.at) && Number.isFinite(paused.seconds)
        ? { at: paused.at, seconds: paused.seconds }
        : undefined;
    return { kept, pausedItself };
  } catch {
    return { kept: [] };
  }
};

const writeLaps = (plannedTaskId: number, laps: Laps) => {
  try {
    window.localStorage.setItem(lapsKey(plannedTaskId), JSON.stringify(laps));
  } catch {
    // Private windows: nothing kept, the laps read as unanswered.
  }
};

export const readCheckIns = (plannedTaskId: number): TimerCheckIns => {
  let answered = 0;
  try {
    answered = Number(window.localStorage.getItem(answeredKey(plannedTaskId))) || 0;
  } catch {
    // Nothing kept: the plan's end.
  }
  return { answered, ...readLaps(plannedTaskId) };
};

/**
 * A check-in answered: by resuming (the student is back) or by "Keep going", which also says they studied through the
 * lap that just ended (check-in 1 ends lap 1, the first past the plan).
 */
export const answerCheckIn = (plannedTaskId: number, checkIn: number, keptGoing = false) => {
  try {
    window.localStorage.setItem(answeredKey(plannedTaskId), String(Math.max(0, checkIn)));
  } catch {
    // Private windows: the pill still knows while the tab is open.
  }
  if (keptGoing && checkIn >= 1) {
    const laps = readLaps(plannedTaskId);
    if (!laps.kept.includes(checkIn)) writeLaps(plannedTaskId, { ...laps, kept: [...laps.kept, checkIn] });
  }
  tell(plannedTaskId);
};

export const notePausedItself = (plannedTaskId: number, pausedItself: PausedItself) => {
  writeLaps(plannedTaskId, { ...readLaps(plannedTaskId), pausedItself });
  tell(plannedTaskId);
};

/** Resumed: it's no longer the timer that paused itself, so its laps tick as any running timer's. */
export const clearPausedItself = (plannedTaskId: number) => {
  const laps = readLaps(plannedTaskId);
  if (!laps.pausedItself) return;
  writeLaps(plannedTaskId, { kept: laps.kept });
  tell(plannedTaskId);
};

/** The timer ended (✓, Reset, another started): its next run starts afresh, its alerts too (timerAlertTabs.ts). */
export const forgetCheckIns = (plannedTaskId: number) => {
  try {
    window.localStorage.removeItem(answeredKey(plannedTaskId));
    window.localStorage.removeItem(lapsKey(plannedTaskId));
    window.localStorage.removeItem(`shovel.timerAlerted.${plannedTaskId}`);
  } catch {
    // Nothing kept, nothing to forget.
  }
  tell(plannedTaskId);
};

// The session's popup showing the laps of a timer that paused itself, for ✓ on the pill to log what they have ticked.
let logLaps: ((button: Element) => boolean) | null = null;

export const onLogLaps = (log: (button: Element) => boolean) => {
  logLaps = log;
  return () => {
    if (logLaps === log) logLaps = null;
  };
};

/** ✓ on the pill with that popup open: the time flies from that ✓. False when nothing was logged, nothing being ticked. */
export const logTickedLaps = (button: Element) => !!logLaps?.(button);
